import { Check, Salad } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface HomeFeedRestrictionsPillProps {
  active: boolean;
  onToggle: () => void;
  /** Registry labels of the tags currently being applied — shown as the subtitle. */
  summary: string;
}

/**
 * Toggle for the feed's automatic "my dietary restrictions" filter.
 *
 * Deliberately NOT part of the active-filter chip strip: those chips are
 * ad-hoc, per-browse choices the user typed in, while this reflects a saved
 * profile preference that re-arms on every return to the feed. Keeping them
 * separate means Reset (which clears the chip strip) doesn't silently drop the
 * user's allergen filter, and toggling this off doesn't look like it deleted
 * something from their profile.
 */
export const HomeFeedRestrictionsPill: React.FC<HomeFeedRestrictionsPillProps> = ({
  active,
  onToggle,
  summary,
}) => {
  const { t } = useTranslation('home');

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      className={`flex w-full items-center gap-2 rounded-full border px-3.5 py-2 text-left transition-colors ${
        active
          ? 'border-brand-primary bg-brand-primary/10 text-foreground'
          : 'border-border bg-transparent text-muted-foreground'
      }`}
    >
      <Salad className={`h-4 w-4 shrink-0 ${active ? 'text-brand-primary' : ''}`} />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium leading-tight">
          {active ? t('feed.restrictions.on') : t('feed.restrictions.off')}
        </span>
        <span className="block truncate text-xs text-muted-foreground">{summary}</span>
      </span>
      {active && <Check className="h-4 w-4 shrink-0 text-brand-primary" />}
    </button>
  );
};
